// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { WireContourService } from '@3d-scene/common/util/wire-contour-service';
import { MaterialsModel } from '@3d-scene/service/materials.model';
import { ArticleManager } from '@article/manager/article.manager';
import { CompositeArticleManager } from '@article/manager/composite-article.manager';
import { ArticleLoadingManager } from '@article/service/article-loading.manager';
import { ArticleMoveManager } from '@project/components/article/manager/article-move.manager';
import { BridgeArticleManager } from '@project/components/article/manager/bridge-article.manager';
import { CarrierBlockManager } from '@project/components/article/manager/carrier-block.manager';
import { ArticlePostParseProcessor } from '@project/components/article/processor/postparse/article-post-parse.processor';
import { ArticleContainerAttachedService } from '@project/components/article/service/article-container-attached.service';
import { ArticlesVisibilityService } from '@project/components/article/service/articles-visibility.service';
import { CreateLabelingHelper } from '@project/components/label/helper/create-labeling.helper';
import { LabelingArticleRotationHelper } from '@project/components/label/helper/labeling-article-rotation.helper';
import { ProjectHelper } from '@project/helper/project.helper';
import { CreateNewProjectWithPresetArticlesManager } from '@project/manager/create-new-project-with-preset-articles.manager';
import { ProjectModel } from '@project/model/project.model';
import { ProjectContentPresenter } from '@shared/presenter/project-content.presenter';
import { LayerManager } from '../manager/layer-manager';
import { SceneManager } from '../manager/scene.manager';
import { ArticleChangedService } from '../service/article-changed.service';
import { ArticleContainer } from './article-container';
import { ConnectionFactory } from './connection.factory';
import { DynamicConnectionCreator } from './dynamic-connection-creator';
import { ContainerService } from './container.service';
import { LabelingUtils } from './util/labeling-utils';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectDrillingHoleService } from '@project/service/project-drilling-hole.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { AttachCodingFingerArticleService } from '@project/components/article/service/attach-coding-finger-article.service';

@Injectable({ providedIn: 'root' })
export class ArticleContainerFactory {

  private _labelingUtils: LabelingUtils;
  private _componentUtils: ContainerService;
  private _projectModel: ProjectModel;
  private _projectModelService: ProjectModelService;
  private _projectArticleService: ProjectArticleService;
  private _projectMetaElementService: ProjectMetaElementService;
  private _projectDrillingHoleService: ProjectDrillingHoleService;
  private _articleManager: ArticleManager;
  private _projectHelper: ProjectHelper;
  private _dynamicConnectionCreator: DynamicConnectionCreator;
  private _createLabelingHelper: CreateLabelingHelper;
  private _articleMoveManager: ArticleMoveManager;
  private _sceneManager: SceneManager;
  private _projectContentPresenter: ProjectContentPresenter;
  private _createNewProjectWithPresetArticlesManager: CreateNewProjectWithPresetArticlesManager;
  private _layerManager: LayerManager;
  private _compositeArticleManager: CompositeArticleManager;
  private _articleLoadingManager: ArticleLoadingManager;
  private _articlesVisibilityService: ArticlesVisibilityService;
  private _bridgeArticleManager: BridgeArticleManager;
  private _carrierBlockManager: CarrierBlockManager;
  private _attachCodingFingerArticleService: AttachCodingFingerArticleService;
  private _connectionFactory: ConnectionFactory;
  private _labelingArticleRotationHelper: LabelingArticleRotationHelper;
  private _articlePostParseProcessor: ArticlePostParseProcessor;
  private _articleContainerAttachedService: ArticleContainerAttachedService;
  private _articleChangedService: ArticleChangedService;
  private _materialsModel: MaterialsModel;
  private _wireContourService: WireContourService;

  private initialized = false;

  public initialize(
    labelingUtils: LabelingUtils,
    componentUtils: ContainerService,
    projectModel: ProjectModel,
    projectModelService: ProjectModelService,
    projectArticleService: ProjectArticleService,
    projectMetaElementService: ProjectMetaElementService,
    projectDrillingHoleService: ProjectDrillingHoleService,
    articleManager: ArticleManager,
    projectHelper: ProjectHelper,
    dynamicConnectionCreator: DynamicConnectionCreator,
    createLabelingHelper: CreateLabelingHelper,
    articleMoveManager: ArticleMoveManager,
    sceneManager: SceneManager,
    projectContentPresenter: ProjectContentPresenter,
    createNewProjectWithPresetArticlesManager: CreateNewProjectWithPresetArticlesManager,
    layerManager: LayerManager,
    compositeArticleManager: CompositeArticleManager,
    articleLoadingManager: ArticleLoadingManager,
    articlesVisibilityService: ArticlesVisibilityService,
    bridgeArticleManager: BridgeArticleManager,
    carrierBlockManager: CarrierBlockManager,
    attachCodingFingerArticleService: AttachCodingFingerArticleService,
    connectionFactory: ConnectionFactory,
    labelingArticleRotationHelper: LabelingArticleRotationHelper,
    articlePostParseProcessor: ArticlePostParseProcessor,
    articleContainerAttachedService: ArticleContainerAttachedService,
    articleChangedService: ArticleChangedService,
    materialsModel: MaterialsModel,
    wireContourService: WireContourService,
  ) {
    this._labelingUtils = labelingUtils;
    this._componentUtils = componentUtils;
    this._projectModel = projectModel;
    this._projectModelService = projectModelService;
    this._projectArticleService = projectArticleService;
    this._projectMetaElementService = projectMetaElementService;
    this._projectDrillingHoleService = projectDrillingHoleService;
    this._articleManager = articleManager;
    this._projectHelper = projectHelper;
    this._dynamicConnectionCreator = dynamicConnectionCreator;
    this._createLabelingHelper = createLabelingHelper;
    this._articleMoveManager = articleMoveManager;
    this._sceneManager = sceneManager;
    this._projectContentPresenter = projectContentPresenter;
    this._createNewProjectWithPresetArticlesManager = createNewProjectWithPresetArticlesManager;
    this._layerManager = layerManager;
    this._compositeArticleManager = compositeArticleManager;
    this._articleLoadingManager = articleLoadingManager;
    this._articlesVisibilityService = articlesVisibilityService;
    this._bridgeArticleManager = bridgeArticleManager;
    this._carrierBlockManager = carrierBlockManager;
    this._attachCodingFingerArticleService = attachCodingFingerArticleService;
    this._connectionFactory = connectionFactory;
    this._labelingArticleRotationHelper = labelingArticleRotationHelper;
    this._articlePostParseProcessor = articlePostParseProcessor;
    this._articleContainerAttachedService = articleContainerAttachedService;
    this._articleChangedService = articleChangedService;
    this._materialsModel = materialsModel;
    this._wireContourService  = wireContourService;
    this.initialized = true;
  }

  public getNewInstance(): ArticleContainer {
    if (!this.initialized) {
      throw new Error('ArticleContainerFactory not initialized!');
    }
    return new ArticleContainer(
      this._labelingUtils,
      this._componentUtils,
      this._projectModel,
      this._projectModelService,
      this._projectArticleService,
      this._projectMetaElementService,
      this._projectDrillingHoleService,
      this._articleManager,
      this._projectHelper,
      this._dynamicConnectionCreator,
      this._createLabelingHelper,
      this._articleMoveManager,
      this._sceneManager,
      this._projectContentPresenter,
      this._createNewProjectWithPresetArticlesManager,
      this._layerManager,
      this._compositeArticleManager,
      this._articleLoadingManager,
      this._articlesVisibilityService,
      this._bridgeArticleManager,
      this._carrierBlockManager,
      this._attachCodingFingerArticleService,
      this._connectionFactory,
      this._labelingArticleRotationHelper,
      this._articlePostParseProcessor,
      this._articleContainerAttachedService,
      this._articleChangedService,
      this._materialsModel,
      this._wireContourService,
    );
  }
}
